import React, { useEffect, useRef } from 'react';

import {
  Cell,
  Column,
  ColumnDef,
  flexRender,
  OnChangeFn,
  Row,
  RowPinningState,
  SortingState,
  Table as TanStackTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router';

import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ACTIVE_ROW_HIGHLIGHT, ROW_HIGHLIGHT_BASE } from '@/util/constants';

import { DataTablePagination } from './data-table-pagination';
import { TableProvider, useTableContext } from './table-context';
import { useColumnSizing } from './useColumnSizing';
import { useSplitTableScroll } from './useSplitTableScroll';

// Clicks on interactive controls inside an href-wrapped row must not trigger the row
// navigation. Bubble-phase, so the control has already handled the click (capture would
// break Radix controls, which skip their own handler once defaultPrevented is set).
// Controls that stop propagation never reach this and must preventDefault themselves.
const INTERACTIVE_SELECTOR = 'button, [role="button"], input, textarea, select, label';

const preventRowLinkNavigation = (e: React.MouseEvent) => {
  if (e.target instanceof Element && e.target.closest(INTERACTIVE_SELECTOR)) {
    e.preventDefault();
  }
};

const isModifiedClick = (e: React.MouseEvent) => e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;

const SKELETON_ROW_INDICES = Array(5).fill(0);

const buildTableStyle = (tableMinWidth: number | undefined): React.CSSProperties => ({
  ...(tableMinWidth !== undefined && { tableLayout: 'fixed' as const }),
  ...(tableMinWidth && { minWidth: tableMinWidth }),
});

interface SplitHeaderTableProps<TData extends object> {
  table: TanStackTable<TData>;
  tableStyle: React.CSSProperties;
  getWidth: (column: Column<TData, unknown>) => number | undefined;
  getMinWidth: (column: Column<TData, unknown>) => number | undefined;
  getMaxWidth: (column: Column<TData, unknown>) => number | undefined;
  rowClassName?: string;
  headClassName?: string;
}

const SplitHeaderTable = <TData extends object>({
  table,
  tableStyle,
  getWidth,
  getMinWidth,
  getMaxWidth,
  rowClassName,
  headClassName,
}: SplitHeaderTableProps<TData>) => (
  <table className="w-full border-separate border-spacing-0 text-sm" style={tableStyle}>
    <TableHeader>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className={cn('h-12', rowClassName)}>
          {headerGroup.headers.map((header) => (
            <TableHead
              key={header.id}
              colSpan={header.colSpan}
              style={{
                width: getWidth(header.column),
                minWidth: getMinWidth(header.column),
                maxWidth: getMaxWidth(header.column),
              }}
              className={cn(
                headClassName,
                header.column.columnDef.meta?.isNumeric ? 'text-right' : 'text-left',
                header.column.columnDef.meta?.className
              )}
            >
              {header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext())}
            </TableHead>
          ))}
        </TableRow>
      ))}
    </TableHeader>
  </table>
);

interface RowClickHandlerArgs<TData> {
  isClickable: boolean;
  href?: string | null;
  onRowClick?: (row: TData) => void;
  row: TData;
}

const createRowClickHandler = <TData,>({
  isClickable,
  href,
  onRowClick,
  row,
}: RowClickHandlerArgs<TData>) => {
  if (!isClickable || !onRowClick) {
    return undefined;
  }

  return (e: React.MouseEvent) => {
    // Modified clicks on an href row belong to the browser (open in new tab/window).
    if (href && isModifiedClick(e)) {
      return;
    }
    onRowClick(row);
  };
};

interface RootProps<TData extends object, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  children: React.ReactNode;
  pagination?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  currentPageIndex?: number;
  setCurrentPageIndex?: (index: number) => void;
  initialPageIndex?: number;
  onPageChange?: (pageIndex: number) => void;
  rowCount?: number;
  onRowClick?: (row: TData) => void;
  onRowHover?: (row: TData) => void;
  getRowClickable?: (row: TData) => boolean;
  /**
   * Renders the row as a Link (null disables it per row) so rows open in new tabs. Can be
   * combined with onRowClick — a plain left click then fires both, so the handler must be
   * side-effect-only (e.g. tracking) or target the same URL as the href.
   */
  getRowHref?: (row: TData) => string | null;
  getRowLinkState?: (row: TData) => object | undefined;
  resourceName?: string;
  isFetchingNextPage?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  emptyStateMessage?: React.ReactNode;
  infiniteContentClassName?: string;
  isLoading?: boolean;
  enableRowPinning?: boolean;
  keepPinnedRows?: boolean;
  onRowPinningChange?: (updater: (prev: RowPinningState) => RowPinningState) => void;
  rowPinning?: RowPinningState;
  getRowId?: (row: TData) => string;
  enableRowHover?: boolean;
  activeRowId?: string;
  autoResetPageIndex?: boolean;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  className?: string;
  /**
   * When true, the table fills the bounded height of its parent (which must be a flex column with
   * `min-h-0`). Root renders an outer flex-column wrapper, the scroll container fills it, and the
   * pagination footer anchors to its bottom. `className` then targets the outer wrapper.
   */
  fill?: boolean;
}

const Root = <TData extends object, TValue>({
  columns,
  data,
  children,
  pagination,
  fetchNextPage,
  hasNextPage,
  currentPageIndex,
  setCurrentPageIndex,
  initialPageIndex,
  onPageChange,
  rowCount,
  onRowClick,
  onRowHover,
  getRowClickable,
  getRowHref,
  getRowLinkState,
  resourceName,
  isFetchingNextPage,
  columnVisibility,
  onColumnVisibilityChange,
  sorting,
  onSortingChange,
  manualSorting,
  emptyStateMessage,
  infiniteContentClassName,
  isLoading,
  enableRowPinning,
  keepPinnedRows,
  onRowPinningChange,
  rowPinning,
  getRowId,
  enableRowHover,
  activeRowId,
  autoResetPageIndex,
  enableRowSelection,
  className,
  fill,
}: RootProps<TData, TValue>) => {
  const tableContainerClassName = fill ? 'min-h-0 flex-1' : className;
  const resolvedInfiniteContentClassName = fill
    ? cn('min-h-0 flex-1', infiniteContentClassName)
    : infiniteContentClassName;
  return (
    <TableProvider<TData, TValue>
      columns={columns}
      data={data}
      pagination={pagination}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      currentPageIndex={currentPageIndex}
      setCurrentPageIndex={setCurrentPageIndex}
      initialPageIndex={initialPageIndex}
      onPageChange={onPageChange}
      rowCount={rowCount}
      onRowClick={onRowClick}
      onRowHover={onRowHover}
      getRowClickable={getRowClickable}
      getRowHref={getRowHref}
      getRowLinkState={getRowLinkState}
      resourceName={resourceName}
      isFetchingNextPage={isFetchingNextPage}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={onColumnVisibilityChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
      manualSorting={manualSorting}
      emptyStateMessage={emptyStateMessage}
      infiniteContentClassName={resolvedInfiniteContentClassName}
      isLoading={isLoading}
      enableRowPinning={enableRowPinning}
      keepPinnedRows={keepPinnedRows}
      onRowPinningChange={onRowPinningChange}
      rowPinning={rowPinning}
      getRowId={getRowId}
      enableRowHover={enableRowHover}
      activeRowId={activeRowId}
      autoResetPageIndex={autoResetPageIndex}
      enableRowSelection={enableRowSelection}
      containerClassName={tableContainerClassName}
    >
      {fill ? <div className={cn('flex min-h-0 flex-col', className)}>{children}</div> : children}
    </TableProvider>
  );
};

interface ToolbarProps<TData extends object> {
  children: (table: ReturnType<typeof useTableContext<TData>>['table']) => React.ReactNode;
}

const Toolbar = <TData extends object>({ children }: ToolbarProps<TData>) => {
  const { table } = useTableContext<TData>();
  return <>{children(table)}</>;
};

const Content = <TData extends object>() => {
  const {
    table,
    onRowClick,
    onRowHover,
    getRowClickable,
    getRowHref,
    getRowLinkState,
    resourceName,
    fetchNextPage,
    hasNextPage,
    emptyStateMessage,
    isLoading,
    enableRowPinning,
    enableRowHover,
    activeRowId,
    containerClassName,
  } = useTableContext<TData>();
  const { t } = useTranslation();
  const { hash } = useLocation();

  // Smart column sizing: uses visible columns so tableMinWidth adjusts when columns are hidden
  const { tableMinWidth, getWidth, getMinWidth, getMaxWidth } = useColumnSizing(
    table.getVisibleLeafColumns().map((c) => c.columnDef)
  );
  const { headerRef, bodyRef } = useSplitTableScroll();

  // Scroll to the item when the hash changes
  useEffect(() => {
    if (!hash) {
      return;
    }

    const scrollToId = hash.split(`${resourceName}-`)[1];
    const element = document.getElementById(`${resourceName}-row-${scrollToId}`);
    if (element) {
      element.scrollIntoView({ block: 'end', behavior: 'smooth' });
      element.classList.add('highlight');

      const timer = setTimeout(() => {
        element.classList.remove('highlight');
      }, 2000);

      return () => clearTimeout(timer);
    } else {
      // If the item is not found, fetch the next page
      if (hasNextPage) {
        fetchNextPage();
      }
    }
  }, [fetchNextPage, hash, hasNextPage, resourceName]);

  const renderRow = (row: Row<TData>) => {
    let rowId = row.id;

    if ('document_id' in row.original) {
      rowId = row.original.document_id as string;
    }

    const href = getRowHref?.(row.original);
    const linkState = getRowLinkState?.(row.original);

    const rowContent = (
      <>
        {row.getVisibleCells().map((cell: Cell<TData, unknown>) => (
          <TableCell
            key={cell.id}
            style={{
              width: getWidth(cell.column),
              minWidth: getMinWidth(cell.column),
              maxWidth: getMaxWidth(cell.column),
            }}
            className={cn(
              cell.column.columnDef.meta?.isNumeric
                ? 'text-right font-mono text-sm leading-5'
                : 'text-left',
              cell.column.columnDef.meta?.className
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </>
    );

    const isActive = activeRowId !== undefined && row.id === activeRowId;
    const isClickable = !!onRowClick && (!getRowClickable || getRowClickable(row.original));

    return (
      <TableRow
        key={row.id}
        data-state={row.getIsSelected() && 'selected'}
        onClick={createRowClickHandler({ isClickable, href, onRowClick, row: row.original })}
        onMouseEnter={() => onRowHover?.(row.original)}
        className={cn(
          ROW_HIGHLIGHT_BASE,
          enableRowHover ? 'group' : '',
          href || isClickable ? 'cursor-pointer' : '',
          isActive && ACTIVE_ROW_HIGHLIGHT
        )}
        id={`${resourceName}-row-${rowId}`}
      >
        {href ? (
          <Link to={href} state={linkState} className="contents" onClick={preventRowLinkNavigation}>
            {rowContent}
          </Link>
        ) : (
          rowContent
        )}
      </TableRow>
    );
  };

  const tableStyle = buildTableStyle(tableMinWidth);

  return (
    <div className={cn('flex min-h-0 w-full flex-col', containerClassName)}>
      <div ref={headerRef} className="overflow-hidden">
        <SplitHeaderTable<TData>
          table={table}
          tableStyle={tableStyle}
          getWidth={getWidth}
          getMinWidth={getMinWidth}
          getMaxWidth={getMaxWidth}
        />
      </div>
      <div
        ref={bodyRef}
        data-slot="table-container"
        className="min-h-0 flex-1 overflow-auto overscroll-none"
      >
        <table className="w-full border-separate border-spacing-0 text-sm" style={tableStyle}>
          <TableBody>
            {isLoading ? (
              // Show loading skeleton rows instead of actual data
              SKELETON_ROW_INDICES.map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`skeleton-row-${index}`}>
                  {table.getAllColumns().map((column, colIndex) => (
                    <TableCell
                      // eslint-disable-next-line react/no-array-index-key
                      key={`skeleton-cell-${colIndex}`}
                      style={{
                        width: getWidth(column),
                        minWidth: getMinWidth(column),
                        maxWidth: getMaxWidth(column),
                      }}
                      className={cn(
                        colIndex === 0 ? 'pl-6' : '',
                        column.columnDef.meta?.isNumeric ? 'text-right' : 'text-left',
                        column.columnDef.meta?.className
                      )}
                    >
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              <>
                {/* Render top pinned rows */}
                {enableRowPinning && table.getTopRows().map((row) => renderRow(row))}

                {/* Render center (unpinned) rows */}
                {table.getCenterRows().map((row) => renderRow(row))}

                {/* Render bottom pinned rows */}
                {enableRowPinning && table.getBottomRows().map((row) => renderRow(row))}
              </>
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="h-24 text-center"
                >
                  {emptyStateMessage || t('analytics.no_data')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>
    </div>
  );
};

const InfiniteContent = <TData extends object>() => {
  const {
    table,
    onRowClick,
    onRowHover,
    getRowClickable,
    getRowHref,
    getRowLinkState,
    resourceName,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    infiniteContentClassName,
    isLoading,
    enableRowHover,
  } = useTableContext<TData>();
  const { t } = useTranslation();
  const observerElem = useRef<HTMLDivElement>(null);

  // Smart column sizing: uses visible columns so tableMinWidth adjusts when columns are hidden
  const { tableMinWidth, getWidth, getMinWidth, getMaxWidth } = useColumnSizing(
    table.getVisibleLeafColumns().map((c) => c.columnDef)
  );
  const { headerRef, bodyRef } = useSplitTableScroll();

  // Intersection Observer for infinite scrolling
  useEffect(() => {
    const currentObserverElem = observerElem.current;
    if (!currentObserverElem) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage?.();
        }
      },
      { root: bodyRef.current, rootMargin: '30px', threshold: 0 }
    );

    observer.observe(currentObserverElem);

    return () => {
      if (currentObserverElem) {
        observer.unobserve(currentObserverElem);
      }
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, bodyRef]);

  const tableStyle = buildTableStyle(tableMinWidth);
  const rows = table.getRowModel().rows;

  return (
    <div className={cn('flex min-h-0 w-full flex-col', infiniteContentClassName)}>
      <div ref={headerRef} className="overflow-hidden">
        <SplitHeaderTable<TData>
          table={table}
          tableStyle={tableStyle}
          getWidth={getWidth}
          getMinWidth={getMinWidth}
          getMaxWidth={getMaxWidth}
          rowClassName="border-none"
          headClassName="bg-surface-card"
        />
      </div>

      <div
        ref={bodyRef}
        data-slot="table-container"
        className="min-h-0 flex-1 overflow-auto overscroll-none"
      >
        <table
          className="relative w-full border-separate border-spacing-0 text-sm"
          style={tableStyle}
        >
          <TableBody>
            {isLoading ? (
              SKELETON_ROW_INDICES.map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`skeleton-row-${index}`}>
                  {table.getAllColumns().map((column, colIndex) => (
                    <TableCell
                      // eslint-disable-next-line react/no-array-index-key
                      key={`skeleton-cell-${colIndex}`}
                      style={{
                        width: getWidth(column),
                        minWidth: getMinWidth(column),
                        maxWidth: getMaxWidth(column),
                      }}
                      className={cn(
                        colIndex === 0 ? 'pl-6' : '',
                        column.columnDef.meta?.isNumeric ? 'text-right' : 'text-left',
                        column.columnDef.meta?.className
                      )}
                    >
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length ? (
              rows.map((row, rowIndex) => {
                let rowId = row.id;

                if ('document_id' in row.original) {
                  rowId = row.original.document_id as string;
                }

                const isLastRow = rowIndex === rows.length - 1;
                const href = getRowHref?.(row.original);
                const linkState = getRowLinkState?.(row.original);
                const isClickable =
                  !!onRowClick && (!getRowClickable || getRowClickable(row.original));

                const rowContent = (
                  <>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{
                          width: getWidth(cell.column),
                          minWidth: getMinWidth(cell.column),
                          maxWidth: getMaxWidth(cell.column),
                        }}
                        className={cn(
                          cell.column.columnDef.meta?.isNumeric
                            ? 'text-right font-mono text-sm leading-5'
                            : 'text-left',
                          cell.column.columnDef.meta?.className
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </>
                );

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={createRowClickHandler({
                      isClickable,
                      href,
                      onRowClick,
                      row: row.original,
                    })}
                    onMouseEnter={() => onRowHover?.(row.original)}
                    className={cn(
                      enableRowHover ? 'group' : '',
                      href || isClickable ? 'cursor-pointer' : '',
                      isLastRow ? 'border-b-0' : ''
                    )}
                    id={`${resourceName}-row-${rowId}`}
                  >
                    {href ? (
                      <Link
                        to={href}
                        state={linkState}
                        className="contents"
                        onClick={preventRowLinkNavigation}
                      >
                        {rowContent}
                      </Link>
                    ) : (
                      rowContent
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getVisibleLeafColumns().length}
                  className="h-24 text-center"
                >
                  {t('analytics.no_data')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>

        {/* Infinite scroll trigger */}
        <div ref={observerElem} />

        {/* Loading indicator */}
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <Spinner size="lg" />
          </div>
        )}
      </div>
    </div>
  );
};

interface PaginationProps {
  centerContent?: React.ReactNode;
}

const Pagination = <TData extends object>({ centerContent }: PaginationProps = {}) => {
  const {
    table,
    fetchNextPage,
    hasNextPage,
    currentPageIndex,
    setCurrentPageIndex,
    rowCount,
    resourceName,
    isFetchingNextPage,
  } = useTableContext<TData>();

  // Hide footer only when there are no rows and no centerContent
  const hasRows = rowCount && rowCount > 0;
  if (!hasRows && !centerContent) {
    return null;
  }

  return (
    <DataTablePagination
      table={table}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      currentPageIndex={currentPageIndex}
      setCurrentPageIndex={setCurrentPageIndex}
      rowCount={rowCount}
      resourceName={resourceName}
      isFetchingNextPage={isFetchingNextPage}
      centerContent={centerContent}
    />
  );
};

export const DataTable = { Root, Toolbar, Content, InfiniteContent, Pagination };
